/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * Shared Inputs Panel Component
 * Manages AI System shared inputs that can be used across all experts via {{ shared.input_name }}
 */

import React, { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Stack,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Autocomplete,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Info as InfoIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  PlayArrow as TestIcon,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { sharedInputApi } from '../../services/api';
import { formatStoredDateTime } from '../../utils/datetime';
import type { SharedInput, SharedInputListItem, SharedInputCreate } from '../../types';

// Reuse input types from PromptEditor
import { InputType, INPUT_TYPE_OPTIONS, INPUT_TYPE_GROUPS, ADMIN_ONLY_INPUT_TYPES } from '../common/PromptEditor';
// Use the shared InputConfigFields component for consistent configuration
import { InputConfigFields } from '../common/InputConfigFields';
import { useAuth } from '../../context/AuthContext';

interface SharedInputsPanelProps {
  systemId: number;
}

interface SharedInputFormData {
  name: string;
  description: string;
  input_type: InputType;
  config: Record<string, any>;
  is_active: boolean;
}

const getApiErrorMessage = (error: any, fallback: string) => {
  const data = error?.response?.data;
  if (!data) {
    return error?.message || fallback;
  }
  if (data.detail || data.error || data.message || data.name?.[0]) {
    return data.detail || data.error || data.message || data.name[0];
  }
  if (data.feature_code && data.reason) {
    return `Your current subscription does not include access to ${String(data.feature_code).replace(/_/g, ' ')}.`;
  }
  return error?.message || fallback;
};

const initialFormData: SharedInputFormData = {
  name: '',
  description: '',
  input_type: 'static_text',
  config: {},
  is_active: true,
};

const SharedInputsPanel: React.FC<SharedInputsPanelProps> = ({ systemId }) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.is_staff === true;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SharedInput | null>(null);
  const [formData, setFormData] = useState<SharedInputFormData>(initialFormData);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingItem, setDeletingItem] = useState<SharedInputListItem | null>(null);
  const {
    data: availableInputTypes,
    isLoading: availableInputTypesLoading,
    isError: availableInputTypesError,
    error: availableInputTypesQueryError,
  } = useQuery({
    queryKey: ['shared-input-types', systemId],
    queryFn: () => sharedInputApi.getAvailableTypes(systemId),
    enabled: !!systemId,
    staleTime: 5 * 60 * 1000,
  });
  const visibleInputTypeOptions = React.useMemo(
    () => {
      const baseOptions = (isAdmin
        ? INPUT_TYPE_OPTIONS
        : INPUT_TYPE_OPTIONS.filter(o => !ADMIN_ONLY_INPUT_TYPES.has(o.value))
      ).filter(opt => opt.value !== 'expert' && opt.value !== 'previous_expert_results');

      if (availableInputTypesError) {
        return [];
      }

      if (!availableInputTypes) {
        return availableInputTypesLoading ? [] : baseOptions;
      }

      const allowedValues = new Set(availableInputTypes.map(type => type.value));
      return baseOptions.filter(option => allowedValues.has(option.value));
    },
    [availableInputTypes, availableInputTypesError, availableInputTypesLoading, isAdmin]
  );
  const selectedInputTypeOption = visibleInputTypeOptions.find(opt => opt.value === formData.input_type)
    || (editingItem ? INPUT_TYPE_OPTIONS.find(opt => opt.value === formData.input_type) : null)
    || null;
  const noAvailableInputTypes = !availableInputTypesLoading && visibleInputTypeOptions.length === 0;
  const inputTypesUnavailableMessage = availableInputTypesError
    ? (availableInputTypesQueryError as any)?.response?.data?.detail
      || (availableInputTypesQueryError as any)?.response?.data?.error
      || 'No shared input adapter types are available for your current subscription.'
    : 'No shared input adapter types are available for your current subscription.';
  
  // Test input state
  const [testResultDialogOpen, setTestResultDialogOpen] = useState(false);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const sharedTokenSx = {
    px: 0.75,
    py: 0.25,
    borderRadius: 0.75,
    bgcolor: alpha(theme.palette.primary.main, isDark ? 0.18 : 0.1),
    color: isDark ? theme.palette.primary.light : theme.palette.primary.dark,
    border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.32 : 0.2)}`,
    fontFamily: 'JetBrains Mono, Consolas, monospace',
    fontSize: '0.85em',
    fontWeight: 600,
  };
  const [testResult, setTestResult] = useState<{
    success: boolean;
    data?: string;
    full_data?: string | null;
    data_type?: string;
    data_length?: number;
    truncated?: boolean;
    execution_time_ms: number;
    error?: string;
    error_type?: string;
    traceback?: string;
  } | null>(null);
  const [isTesting, setIsTesting] = useState(false);

  // Fetch shared inputs
  const { data: sharedInputsData, isLoading, error } = useQuery({
    queryKey: ['shared-inputs', systemId],
    queryFn: () => sharedInputApi.list(systemId),
    enabled: !!systemId,
  });

  // Handle paginated response - API returns { results: [...], count: X }
  const items = sharedInputsData?.results || [];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: SharedInputCreate) => sharedInputApi.create(systemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-inputs', systemId] });
      toast.success('Shared input created');
      handleCloseDialog();
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to create shared input'));
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<SharedInputCreate> }) => 
      sharedInputApi.update(systemId, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-inputs', systemId] });
      toast.success('Shared input updated');
      handleCloseDialog();
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to update shared input'));
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => sharedInputApi.delete(systemId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['shared-inputs', systemId] });
      toast.success('Shared input deleted');
      setDeleteDialogOpen(false);
      setDeletingItem(null);
    },
    onError: (error: any) => {
      toast.error(getApiErrorMessage(error, 'Failed to delete shared input'));
    },
  });

  const handleOpenDialog = useCallback(async (item?: SharedInputListItem) => {
    if (item) {
      try {
        const fullItem = await sharedInputApi.get(systemId, item.id);
        setEditingItem(fullItem);
        setFormData({
          name: fullItem.name,
          description: fullItem.description || '',
          input_type: fullItem.input_type as InputType,
          config: fullItem.config || {},
          is_active: fullItem.is_active,
        });
      } catch (err) {
        toast.error('Failed to load shared input details');
        return;
      }
    } else {
      setEditingItem(null);
      setFormData(initialFormData);
    }
    setDialogOpen(true);
  }, [systemId]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingItem(null);
    setFormData(initialFormData);
  };

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    // Validate name format (snake_case)
    if (!/^[a-z][a-z0-9_]*$/.test(formData.name)) {
      toast.error('Name must be lowercase with underscores (snake_case), starting with a letter');
      return;
    }

    const submitData: SharedInputCreate = {
      name: formData.name,
      description: formData.description,
      input_type: formData.input_type,
      config: formData.config,
      is_active: formData.is_active,
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleCopyVariable = (name: string) => {
    const variable = `{{ shared.${name} }}`;
    navigator.clipboard.writeText(variable);
    toast.success(`Copied: ${variable}`);
  };

  const handleTestInput = async () => {
    if (!formData.input_type) {
      toast.error('Please select an input type first');
      return;
    }

    setIsTesting(true);
    try {
      const result = await sharedInputApi.testInput(systemId, formData.input_type, formData.config);
      setTestResult(result);
      setTestResultDialogOpen(true);
    } catch (error: any) {
      const message = getApiErrorMessage(error, 'Failed to test input');
      toast.error(message);
      setTestResult({
        success: false,
        error: message,
        error_type: 'RequestError',
        execution_time_ms: 0,
      });
      setTestResultDialogOpen(true);
    } finally {
      setIsTesting(false);
    }
  };

  const getTypeLabel = (inputType: string): string => {
    const option = INPUT_TYPE_OPTIONS.find(opt => opt.value === inputType);
    return option?.label || inputType;
  };

  const getTypeColor = (inputType: string): string => {
    const option = INPUT_TYPE_OPTIONS.find(opt => opt.value === inputType);
    const group = INPUT_TYPE_GROUPS.find(g => g.name === option?.group);
    return group?.color || '#6b7280';
  };

  const formatDate = (dateStr: string | undefined) => {
    return formatStoredDateTime(dateStr, undefined, 'N/A');
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        Failed to load shared inputs: {(error as Error).message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h6">Shared Inputs</Typography>
          <Typography variant="body2" color="text.secondary">
            Define inputs that can be used across all experts in this system
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          disabled={noAvailableInputTypes}
        >
          Add Shared Input
        </Button>
      </Box>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          <strong>Usage:</strong> Access shared inputs in expert prompts using{' '}
          <Box component="code" sx={sharedTokenSx}>
            {'{{ shared.input_name }}'}
          </Box>
          {' '}syntax.
        </Typography>
      </Alert>

      {noAvailableInputTypes && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {inputTypesUnavailableMessage}
        </Alert>
      )}

      {/* Items Table */}
      {items.length === 0 ? (
        <Card>
          <CardContent>
            <Box textAlign="center" py={4}>
              <Typography color="text.secondary" gutterBottom>
                No shared inputs yet
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Shared inputs let you define data sources once and use them in multiple experts.
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => handleOpenDialog()}
                disabled={noAvailableInputTypes}
                sx={{ mt: 2 }}
              >
                Add Your First Shared Input
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Template Variable</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Updated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id} hover>
                  <TableCell>
                    <Typography fontWeight="medium">{item.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getTypeLabel(item.input_type)}
                      size="small"
                      sx={{
                        backgroundColor: `${getTypeColor(item.input_type)}20`,
                        color: getTypeColor(item.input_type),
                        fontWeight: 500,
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {item.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={`{{ shared.${item.name} }}`}
                      size="small"
                      variant="outlined"
                      onClick={() => handleCopyVariable(item.name)}
                      sx={{ 
                        fontFamily: 'monospace', 
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                      icon={<CopyIcon sx={{ fontSize: 14 }} />}
                    />
                  </TableCell>
                  <TableCell>
                    {item.is_active ? (
                      <Chip
                        icon={<ActiveIcon />}
                        label="Active"
                        size="small"
                        color="success"
                        variant="outlined"
                      />
                    ) : (
                      <Chip
                        icon={<InactiveIcon />}
                        label="Inactive"
                        size="small"
                        color="default"
                        variant="outlined"
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(item.updated_at)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={() => handleCopyVariable(item.name)}>
                      <CopyIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenDialog(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeletingItem(item);
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Shared Input' : 'Add Shared Input'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            {editingItem
              ? 'Update the shared input configuration.'
              : 'Define a shared input that can be used across all experts in this system using {{ shared.input_name }} syntax.'
            }
          </Typography>
          <Stack spacing={2} mt={1}>
            <TextField
              autoFocus
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
              placeholder="e.g., market_data, user_context"
              helperText="Use lowercase with underscores (snake_case)"
              disabled={!!editingItem}
              error={formData.name.length > 0 && !/^[a-z][a-z0-9_]*$/.test(formData.name)}
            />

            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe what this input provides"
              multiline
              rows={2}
            />

            {/* Input Type Selector */}
            <Autocomplete
              fullWidth
              disabled={!!editingItem || noAvailableInputTypes}
              options={visibleInputTypeOptions}
              groupBy={(option) => option.group}
              getOptionLabel={(option) => option.label}
              value={selectedInputTypeOption}
              onChange={(_e, newValue) => {
                if (newValue) {
                  setFormData({ ...formData, input_type: newValue.value, config: {} });
                }
              }}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Input Type"
                  placeholder="Search input types..."
                  helperText={
                    editingItem
                      ? 'Input type cannot be changed after creation'
                      : availableInputTypesLoading
                        ? 'Loading available input types...'
                        : noAvailableInputTypes
                          ? inputTypesUnavailableMessage
                          : 'Select the type of data this input provides'
                  }
                />
              )}
              renderGroup={(params) => {
                const groupInfo = INPUT_TYPE_GROUPS.find(g => g.name === params.group);
                return (
                  <li key={params.key}>
                    <Box
                      sx={{
                        position: 'sticky',
                        top: '-8px',
                        padding: '8px 16px',
                        backgroundColor: 'background.paper',
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        zIndex: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 'bold',
                          color: groupInfo?.color || 'text.secondary',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                        }}
                      >
                        {groupInfo?.icon} {params.group}
                      </Typography>
                    </Box>
                    <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
                  </li>
                );
              }}
              renderOption={(props, option) => {
                const groupInfo = INPUT_TYPE_GROUPS.find(g => g.name === option.group);
                return (
                  <li {...props} key={option.value}>
                    <Box sx={{ width: '100%', py: 0.5 }}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: groupInfo?.color || 'grey.400',
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="body2" fontWeight="medium">
                          {option.label}
                        </Typography>
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ pl: 2, display: 'block' }}
                      >
                        {option.description}
                      </Typography>
                    </Box>
                  </li>
                );
              }}
              slotProps={{
                paper: {
                  sx: {
                    maxHeight: 400,
                    '& .MuiAutocomplete-listbox': {
                      padding: 0,
                    },
                  },
                },
              }}
            />

            {/* Type-Specific Configuration Fields - Using shared component */}
            <Box sx={{ mt: 2, p: 2, bgcolor: isDark ? 'grey.900' : 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Configuration
              </Typography>
              <Stack spacing={2}>
                <InputConfigFields
                  inputType={formData.input_type}
                  config={formData.config}
                  onConfigChange={(newConfig) => setFormData({ ...formData, config: newConfig })}
                  hideExpertFields={true}
                />
              </Stack>
            </Box>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
              }
              label="Active"
            />

            {/* Preview */}
            {formData.name && (
              <Alert severity="info" icon={<InfoIcon />}>
                <Typography variant="body2">
                  <strong>Usage:</strong> Reference this input in expert prompts as{' '}
                  <Box component="code" sx={sharedTokenSx}>
                    {`{{ shared.${formData.name} }}`}
                  </Box>
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleTestInput}
            disabled={isTesting || !formData.input_type}
            startIcon={isTesting ? <CircularProgress size={16} /> : <TestIcon />}
          >
            Test Input
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {createMutation.isPending || updateMutation.isPending ? (
              <CircularProgress size={20} />
            ) : editingItem ? (
              'Update'
            ) : (
              'Create'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Shared Input</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the shared input "{deletingItem?.name}"?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Experts using{' '}
            <Box component="code" sx={sharedTokenSx}>
              {`{{ shared.${deletingItem?.name} }}`}
            </Box>{' '}
            will no longer receive this data.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => deletingItem && deleteMutation.mutate(deletingItem.id)}
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Result Dialog */}
      <Dialog 
        open={testResultDialogOpen} 
        onClose={() => setTestResultDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Test Input Result
          {testResult && (
            <Chip
              size="small"
              label={testResult.success ? 'Success' : 'Error'}
              color={testResult.success ? 'success' : 'error'}
              sx={{ ml: 2 }}
            />
          )}
        </DialogTitle>
        <DialogContent>
          {testResult && (
            <Stack spacing={2}>
              {/* Metadata */}
              <Box display="flex" gap={2} flexWrap="wrap">
                <Chip 
                  label={`Type: ${formData.input_type}`} 
                  size="small" 
                  variant="outlined"
                />
                <Chip 
                  label={`Execution: ${testResult.execution_time_ms}ms`} 
                  size="small" 
                  variant="outlined"
                  color="info"
                />
                {testResult.data_type && (
                  <Chip 
                    label={`Data Type: ${testResult.data_type}`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
                {testResult.data_length !== undefined && (
                  <Chip 
                    label={`Length: ${testResult.data_length.toLocaleString()} chars`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
                {testResult.truncated && (
                  <Chip 
                    label="Truncated" 
                    size="small" 
                    color="warning"
                  />
                )}
              </Box>

              {/* Result Data or Error */}
              {testResult.success ? (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Output Data:
                  </Typography>
                  <Paper 
                    variant="outlined" 
                    sx={{ 
                      p: 2, 
                      maxHeight: 400, 
                      overflow: 'auto',
                      bgcolor: isDark ? 'grey.900' : 'grey.50',
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {testResult.data || '(empty result)'}
                  </Paper>
                </Box>
              ) : (
                <Box>
                  <Alert severity="error" sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">
                      {testResult.error_type}: {testResult.error}
                    </Typography>
                  </Alert>
                  {testResult.traceback && (
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Stack Trace:
                      </Typography>
                      <Paper 
                        variant="outlined" 
                        sx={{ 
                          p: 2, 
                          maxHeight: 300, 
                          overflow: 'auto',
                          bgcolor: isDark ? 'grey.800' : 'grey.100',
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'pre-wrap',
                        }}
                      >
                        {testResult.traceback}
                      </Paper>
                    </Box>
                  )}
                </Box>
              )}

              {/* Copy Button for successful results */}
              {testResult.success && testResult.data && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<CopyIcon />}
                  onClick={() => {
                    // Use full_data if available (not truncated), otherwise use displayed data
                    const dataToCopy = testResult.full_data || testResult.data || '';
                    navigator.clipboard.writeText(dataToCopy);
                    toast.success('Output copied to clipboard');
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Copy Output
                </Button>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestResultDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SharedInputsPanel;
